import { useTranslate } from "@pathwright/ui/src/components/lng/withTranslate"
import Pathicon from "@pathwright/ui/src/components/pathicon/Pathicon"
import { useScreensizeContext } from "@pathwright/ui/src/components/ui/Screensize"
import defaults from "lodash/defaults"
import get from "lodash/get"
import moment from "moment"
import PropTypes from "prop-types"
import styled from "styled-components"

const MODE_INFO = 1
const MODE_ACTIONABLE = 2

const Divider = styled.div`
  margin: 0 3px;
`
const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  ${(p) => p.inverted && "color: white;"}

  .Pathicon:not(:only-child) {
    margin-right: 0.25em;
  }

  ${(p) =>
    p.collapse &&
    `
      @media (max-width: 320px) {
        flex-direction: column;
        & ${Divider} {
          display: none;
        }
        & ${InnerWrapper} + ${InnerWrapper} {
          margin-top:0.25em;
        }
      }
  `}
`
const InnerWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
`
const Label = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`
const Month = styled.span`
  font-size: 8px;
  line-height: 1em;
  font-weight: 600;
`
const Day = styled.span`
  font-size: 12px;
  line-height: 1em;
  font-weight: 500;
`

const actionIcon = "calendar-plus"

const getStartsIcon = ({ starts, passive_starts, mode }) => {
  switch (mode) {
    case MODE_INFO:
      if (starts && !passive_starts) return "lock"
      break
    case MODE_ACTIONABLE:
      if (starts && !passive_starts) return "calendar-lock"
      break
  }

  return null
}

const getDueIcon = ({ due, passive_due, mode }) => {
  switch (mode) {
    case MODE_INFO:
      if (due && !passive_due) return "flag"
      break
    case MODE_ACTIONABLE:
      if (due && !passive_due) return "calendar-flag"
      break
  }

  return null
}

const PathScheduleLabel = ({
  pathItem,
  mode,
  showIcon,
  showNotScheduled,
  inverted,
  collapse,
  ...pathItemProps
}) => {
  const screensize = useScreensizeContext()
  const { tc } = useTranslate()
  const { starts, due, passive_starts, passive_due } = defaults(
    pathItemProps,
    pathItem
  )

  const startsIcon = showIcon
    ? getStartsIcon({
        starts,
        passive_starts,
        mode
      })
    : null
  const dueIcon = showIcon
    ? getDueIcon({
        due,
        passive_due,
        mode
      })
    : null
  const showDivider =
    !!starts && moment(starts).isValid() && !!due && moment(due).isValid()

  // completed user path item
  if (
    mode === MODE_INFO &&
    get(pathItem, "completion.user") &&
    get(pathItem, "progress") >= 1
  )
    return null

  // path item has no dates
  if (!due && !starts) {
    if (showNotScheduled) {
      return (
        <Wrapper inverted={inverted}>
          <span>{tc("not scheduled")}</span>
        </Wrapper>
      )
    }
    if (mode === MODE_ACTIONABLE) {
      return (
        <Wrapper inverted={inverted}>
          <Pathicon icon={actionIcon} />
        </Wrapper>
      )
    }
    return null
  }

  // Conditionally include the year when the date is not for the current year.
  function formatDate(momentDate) {
    if (momentDate.year() === new Date().getFullYear()) {
      return momentDate.format(`MMM ${screensize === "sm" ? "D" : "Do"}, h:mma`)
    } else {
      return momentDate.format(
        `MMM ${screensize === "sm" ? "D" : "Do"}, ${
          screensize === "sm" ? "'YY" : "YYYY"
        }, h:mma`
      )
    }
  }

  return mode === MODE_ACTIONABLE ? (
    <Wrapper inverted={inverted}>
      {startsIcon && <Pathicon icon={startsIcon} />}
      {!!starts && moment(starts).isValid() && (
        <Label>
          <Month>{moment(starts).format("MMM")}</Month>
          <Day>{moment(starts).format("DD")}</Day>
        </Label>
      )}
      {showDivider && <Divider>–</Divider>}
      {dueIcon && <Pathicon icon={dueIcon} />}
      {!!due && moment(due).isValid() && (
        <Label>
          <Month>{moment(due).format("MMM")}</Month>
          <Day>{moment(due).format("DD")}</Day>
        </Label>
      )}
    </Wrapper>
  ) : (
    <Wrapper inverted={inverted} collapse={collapse}>
      <InnerWrapper>
        {startsIcon && <Pathicon icon={startsIcon} />}
        {!!starts && moment(starts).isValid() && (
          <div>{formatDate(moment(starts))}</div>
        )}
        {showDivider && <Divider>–</Divider>}
      </InnerWrapper>
      <InnerWrapper>
        {dueIcon && <Pathicon icon={dueIcon} />}
        {!!due && moment(due).isValid() && <div>{formatDate(moment(due))}</div>}
      </InnerWrapper>
    </Wrapper>
  )
}

PathScheduleLabel.displayName = "PathScheduleLabel"

PathScheduleLabel.propTypes = {
  pathItem: PropTypes.object, // optionally pass pathItem
  showIcon: PropTypes.bool,
  showNotScheduled: PropTypes.bool,
  mode: PropTypes.oneOf([MODE_INFO, MODE_ACTIONABLE]),
  starts: PropTypes.string, // optionally pass to override pathItem
  due: PropTypes.string, // optionally pass to override pathItem
  passive_starts: PropTypes.bool, // optionally pass to override pathItem
  passive_due: PropTypes.bool // optionally pass to override pathItem
}

PathScheduleLabel.defaultProps = {
  mode: MODE_INFO,
  collapse: false, // style hack...
  showIcon: false,
  showNotScheduled: false
}

PathScheduleLabel.actionIcon = actionIcon
PathScheduleLabel.getStartsIcon = getStartsIcon
PathScheduleLabel.getDueIcon = getDueIcon
PathScheduleLabel.modes = {
  info: MODE_INFO,
  actionable: MODE_ACTIONABLE
}

export default PathScheduleLabel
